import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import SecureImage from '../../components/Reusables/SecureImage';
import { AppAlert } from '../../services/AppAlert';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { List } from 'react-native-paper';
import {
  GTFonts,
  LatoFonts,
  PoppinsFonts,
  useTheme,
  useThemeColors,
  withOpacity,
} from '../../theme';
import { fs, s, v, clearFastImageCache, iconSize } from '../../utils/libs';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { RootState } from '../../redux/types';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { AuthContext } from '../../Context/AuthContext';
import { logoutAction } from '../../redux/Store';
import {
  clearEmployeeAndBusinessInfo,
  setEmployeeLocalProfileImage,
  setEmployeeRemoteProfileImageUrl,
} from '../../redux/AuthSlice';
import DeviceInfo from 'react-native-device-info';
import { uploadEmployeeProfilePictureUrl } from '../../api/employees';
import { useSecureNavigate } from '../../navigation/security/useSecureNavigate';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';
import { isEmployeeAdminRole } from '../../utils/roles';
import { getApiMessage } from '../../utils/apiError';

const EmployeeProfileScreen: React.FC = () => {
  const { theme } = useTheme();
  const colors = useThemeColors();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);
  const isWeb = Platform.OS === 'web';
  const itemHovered = isWeb
    ? { backgroundColor: theme.colors.surfaceSecondary }
    : undefined;
  const employee = useAppSelector(
    (s: RootState) => s.auth.inApp.account.employeeInfo,
  );
  const navigation = useNavigation<any>();
  const { secureNavigate } = useSecureNavigate(navigation);
  const dispatch = useAppDispatch();
  const axiosprivate = useAxiosPrivate();
  const { auth, setAuth } = useContext(AuthContext);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isAdmin = isEmployeeAdminRole(employee?.bp_employee_role);
  const isIOS = Platform.OS === 'ios';

  // Web-only: file input ref for inline photo picker
  const webFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const photoUrl =
    employee?.bp_employee_profile_picture_local_uri ||
    employee?.bp_employee_profile_picture_url ||
    '';

  const [appVersion, setAppVersion] = useState('');

  useEffect(() => {
    const v = String(DeviceInfo.getVersion() || '').trim();
    if (v) {
      setAppVersion(v.replace('-prod', '').trim());
    }
  }, []);
  const onChangePhoto = useCallback(() => {
    if (Platform.OS === 'web') {
      webFileInputRef.current?.click();
      return;
    }
    navigation.navigate('EmployeeSelfie');
  }, [navigation]);

  // Web-only: handle file selection and upload
  const handleWebFileChange = useCallback(
    async (e: any) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      e.target.value = '';

      setUploadingPhoto(true);
      try {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        dispatch(setEmployeeLocalProfileImage(previewUrl));

        // Resize and convert to base64 using Canvas
        const base64 = await new Promise<string>((resolve, reject) => {
          const img = document.createElement('img');
          img.onload = () => {
            URL.revokeObjectURL(img.src);
            let { width, height } = img;
            const MAX = 1080;
            if (width > MAX || height > MAX) {
              const ratio = Math.min(MAX / width, MAX / height);
              width = Math.floor(width * ratio);
              height = Math.floor(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Canvas unavailable'));
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
            resolve(dataUrl.split(',')[1]);
          };
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = URL.createObjectURL(file);
        });

        // Upload to server
        const res = await uploadEmployeeProfilePictureUrl(
          axiosprivate,
          employee?._id,
          base64,
        );
        const remoteUrl = res?.data?.data?.profile_picture_url;
        if (remoteUrl) {
          dispatch(setEmployeeRemoteProfileImageUrl(remoteUrl));
        }
        AppAlert.alert('Success', 'Profile photo updated.');
      } catch (err: any) {
        AppAlert.alert('Error', getApiMessage(err, 'Failed to upload photo'));
      } finally {
        setUploadingPhoto(false);
      }
    },
    [axiosprivate, dispatch, employee?._id],
  );

  const clearAll = useCallback(async () => {
    await clearFastImageCache();
    dispatch(clearEmployeeAndBusinessInfo());
    dispatch(logoutAction());
  }, [dispatch, setAuth]);

  const ClearSecrets = async () => {
    await AsyncStorage.multiRemove(['accessToken', 'refreshToken']);
    setAuth({ accessToken: '', refreshToken: '' });
  };

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      const refreshToken = await AsyncStorage.getItem('refreshToken');
      await clearAll();
      await axiosprivate.post(
        `bp_employee_log_out_information/${refreshToken}`,
        {},
        {
          headers: { Authorization: 'Bearer ' + auth.accessToken },
        },
      );
      await ClearSecrets();
    } catch (e) {
      // ignore API failure; proceed to local clear
    } finally {
      setIsLoggingOut(false);
    }
  }, [axiosprivate, auth?.accessToken, clearAll, isLoggingOut]);

  const confirmLogout = useCallback(() => {
    AppAlert.alert('Log out', 'Are you sure you want to log out?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Log out', style: 'destructive', onPress: handleLogout },
    ]);
  }, [handleLogout]);

  return (
    <View style={styles.screen}>
      {/* Web-only: hidden file input for photo picker */}
      {Platform.OS === 'web' && (
        <input
          ref={webFileInputRef as any}
          type="file"
          accept="image/*"
          onChange={handleWebFileChange}
          style={{ display: 'none' }}
        />
      )}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.container,
          constrainWidth ? styles.containerConstrained : null,
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <View style={styles.avatarWrap}>
            {photoUrl ? (
              <SecureImage uri={photoUrl} style={styles.avatar} />
            ) : (
              <View style={[styles.avatar, styles.avatarFallback]}>
                <MaterialCommunityIcons
                  name="account"
                  size={iconSize(36)}
                  color={theme.colors.textSecondary}
                />
              </View>
            )}
            <TouchableOpacity
              style={[styles.editBtn, uploadingPhoto && { opacity: 0.6 }]}
              onPress={onChangePhoto}
              disabled={uploadingPhoto}>
              <MaterialCommunityIcons
                name={uploadingPhoto ? 'loading' : 'camera'}
                size={iconSize(16)}
                color={theme.colors.textInverse}
              />
            </TouchableOpacity>
          </View>
          <Text style={styles.name}>
            {String(employee?.bp_employee_name || '').trim() || 'Your Name'}
          </Text>
          <Text style={styles.subtle}>
            {String(employee?.bp_employee_role || '')}
          </Text>
        </View>

        {isAdmin ? (
          <>
            <List.Section>
              <Pressable
                onPress={() => void secureNavigate('BackOfficeUserProfile')}
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemWeb,
                  isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="User Profile"
                  description="Manage your personal details"
                  left={props => (
                    <MaterialCommunityIcons
                      name="account"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  titleStyle={styles.itemTitle}
                />
              </Pressable>
              <Pressable
                onPress={() => void secureNavigate('BackOfficeCompanyProfile')}
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemWeb,
                  isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Business Profile"
                  description="Update business and address info"
                  left={props => (
                    <MaterialCommunityIcons
                      name="office-building"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  titleStyle={styles.itemTitle}
                />
              </Pressable>
              <Pressable
                disabled={isWeb}
                onPress={
                  isWeb
                    ? undefined
                    : () => void secureNavigate('SecuritySettingsScreen')
                }
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemDisabled,
                  !isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Security"
                  description={
                    isWeb
                      ? 'Biometrics and access~Coming Soon'
                      : 'Biometrics and access'
                  }
                  left={props => (
                    <MaterialCommunityIcons
                      name="shield-lock-outline"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  disabled={isWeb}
                  titleStyle={styles.itemTitle}
                />
              </Pressable>
              <Pressable
                onPress={() => navigation.navigate('SettingsScreen')}
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemWeb,
                  isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Settings"
                  description="Preferences and device settings"
                  left={props => (
                    <Ionicons
                      name="settings-sharp"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  titleStyle={styles.singleLineTitle}
                />
              </Pressable>
              {!isIOS && (
                <Pressable
                  onPress={() => void secureNavigate('BackOfficeBilling')}
                  style={({ pressed, hovered }: any) => [
                    styles.item,
                    isWeb && styles.itemWeb,
                    isWeb && hovered && itemHovered,
                    pressed && { opacity: 0.85 },
                  ]}>
                  <List.Item
                    title="Billing"
                    left={props => (
                      <MaterialCommunityIcons
                        name="credit-card"
                        size={iconSize(18)}
                        color={theme.colors.text}
                        style={[props.style, styles.listIcon]}
                      />
                    )}
                    right={props => (
                      <Ionicons
                        name="chevron-forward"
                        size={iconSize(18)}
                        color={theme.colors.textSecondary}
                        style={[props.style, styles.listIcon]}
                      />
                    )}
                    titleStyle={styles.singleLineTitle}
                  />
                </Pressable>
              )}
            </List.Section>

            <List.Section>
              <List.Subheader style={styles.sectionHeader}>
                Help & Support
              </List.Subheader>
              <Pressable
                onPress={() => void secureNavigate('BackOfficeSupport')}
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemWeb,
                  isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Support"
                  left={props => (
                    <MaterialCommunityIcons
                      name="lifebuoy"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  titleStyle={styles.singleLineTitle}
                />
              </Pressable>
              <List.Item
                title="App version"
                left={props => (
                  <MaterialCommunityIcons
                    name="information-outline"
                    size={iconSize(18)}
                    color={theme.colors.text}
                    style={[props.style, styles.listIcon]}
                  />
                )}
                right={() => (
                  <Text style={styles.itemValue}>
                    {appVersion ? `v ${appVersion}` : 'v'}
                  </Text>
                )}
                style={styles.item}
                titleStyle={styles.singleLineTitle}
              />
            </List.Section>
          </>
        ) : (
          <>
            <List.Section>
              <Pressable
                onPress={() => navigation.navigate('SettingsScreen')}
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemWeb,
                  isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Settings"
                  description="Preferences and device settings"
                  left={props => (
                    <Ionicons
                      name="settings-sharp"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  titleStyle={styles.singleLineTitle}
                />
              </Pressable>
              <Pressable
                disabled={isWeb}
                onPress={
                  isWeb
                    ? undefined
                    : () => void secureNavigate('SecuritySettingsScreen')
                }
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemDisabled,
                  !isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Security"
                  description={
                    isWeb
                      ? 'Biometrics and access~Coming Soon'
                      : 'Biometrics and access'
                  }
                  left={props => (
                    <MaterialCommunityIcons
                      name="shield-lock-outline"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  disabled={isWeb}
                  titleStyle={styles.singleLineTitle}
                />
              </Pressable>
            </List.Section>
            <List.Section>
              <List.Subheader style={styles.sectionHeader}>
                Help & Support
              </List.Subheader>
              <Pressable
                onPress={() => void secureNavigate('BackOfficeSupport')}
                style={({ pressed, hovered }: any) => [
                  styles.item,
                  isWeb && styles.itemWeb,
                  isWeb && hovered && itemHovered,
                  pressed && { opacity: 0.85 },
                ]}>
                <List.Item
                  title="Support"
                  description="Get help"
                  left={props => (
                    <MaterialCommunityIcons
                      name="lifebuoy"
                      size={iconSize(18)}
                      color={theme.colors.text}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  right={props => (
                    <Ionicons
                      name="chevron-forward"
                      size={iconSize(18)}
                      color={theme.colors.textSecondary}
                      style={[props.style, styles.listIcon]}
                    />
                  )}
                  titleStyle={styles.singleLineTitle}
                />
              </Pressable>
              <List.Item
                title="App version"
                left={props => (
                  <MaterialCommunityIcons
                    name="information-outline"
                    size={iconSize(18)}
                    color={theme.colors.text}
                    style={[props.style, styles.listIcon]}
                  />
                )}
                right={() => (
                  <Text style={styles.itemValue}>
                    {appVersion ? `v ${appVersion}` : 'v'}
                  </Text>
                )}
                style={styles.item}
                titleStyle={styles.singleLineTitle}
              />
            </List.Section>
          </>
        )}

        <Pressable
          onPress={confirmLogout}
          disabled={isLoggingOut}
          style={({ pressed, hovered }: any) => [
            styles.logoutBtn,
            isWeb && styles.itemWeb,
            isWeb && hovered && !isLoggingOut && styles.logoutBtnHovered,
            pressed && !isLoggingOut && { opacity: 0.85 },
          ]}>
          <MaterialCommunityIcons
            name="logout"
            size={iconSize(18)}
            color={theme.colors.error}
          />
          <Text style={styles.logoutText}>
            {isLoggingOut ? 'Logging out…' : 'Log Out'}
          </Text>
        </Pressable>
      </ScrollView>
    </View>
  );
};

export default EmployeeProfileScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    listIcon: {
      alignSelf: 'center',
    },
    screen: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    scroll: {
      flex: 1,
    },
    container: {
      padding: s(12),
      backgroundColor: theme.colors.surface,
      flexGrow: 1,
    },
    containerConstrained: {
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      alignSelf: 'center',
      width: '100%',
      padding: s(24),
    },
    header: {
      alignItems: 'center',
      marginBottom: v(8),
    },
    avatarWrap: {
      width: s(84),
      height: s(84),
      borderRadius: s(42),
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
    },
    avatar: {
      width: s(84),
      height: s(84),
      borderRadius: s(42),
      backgroundColor: theme.colors.surface,
    },
    avatarFallback: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    editBtn: {
      position: 'absolute',
      right: s(2),
      bottom: v(2),
      width: s(28),
      height: s(28),
      borderRadius: s(14),
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: 'rgba(255,255,255,0.8)',
    },
    name: {
      marginTop: v(6),
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      fontFamily: PoppinsFonts.semibold,
    },
    sectionHeader: {
      fontFamily: PoppinsFonts.semibold,
      color: theme.colors.textSecondary,
    },
    subtle: {
      fontSize: fs(12),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      marginTop: v(2),
    },
    item: {
      backgroundColor: 'rgba(0,0,0,0.03)',
      borderRadius: theme.borderRadius.md,
      marginBottom: v(6),
    },
    itemWeb: {
      ...(Platform.OS === 'web'
        ? ({ cursor: 'pointer', transition: 'all 0.15s ease-out' } as any)
        : {}),
    },
    itemDisabled: {
      opacity: 0.55,
    },
    itemTitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginBottom: v(5),
    },
    singleLineTitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    itemValue: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    logoutBtn: {
      marginTop: v(12),
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      alignSelf: 'stretch',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(12),
      paddingVertical: s(12),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.borderSecondary,
    },
    logoutBtnHovered: {
      backgroundColor: withOpacity(theme.colors.error, 0.08),
    },
    logoutText: {
      color: theme.colors.error,
      fontFamily: PoppinsFonts.semibold,
      fontSize: fs(14),
    },
  });
